import { useCallback, useState } from 'react';

/**
 * Roomy rows carry a second line — the recent rounds, the rest of the people,
 * the whole of a note. Tight rows fit more funds on the screen. Neither is a
 * setting about the data, so it lives beside the column order: per browser.
 */
export type RowDensity = 'comfortable' | 'compact';

interface Preferences {
  order: readonly string[];
  widths: Readonly<Record<string, number>>;
  density: RowDensity;
}

const EMPTY: Preferences = { order: [], widths: {}, density: 'compact' };

function read(key: string): Preferences {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === null) return EMPTY;
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY;
    const order = 'order' in parsed && Array.isArray(parsed.order) ? parsed.order : [];
    const widths = 'widths' in parsed && typeof parsed.widths === 'object' ? parsed.widths : {};
    return {
      order: order.filter((value): value is string => typeof value === 'string'),
      widths: Object.fromEntries(
        Object.entries(widths ?? {}).filter(
          (entry): entry is [string, number] => typeof entry[1] === 'number',
        ),
      ),
      density: 'density' in parsed && parsed.density === 'comfortable' ? 'comfortable' : 'compact',
    };
  } catch {
    // A private window or a cleared store is simply no preference.
    return EMPTY;
  }
}

function write(key: string, preferences: Preferences): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(preferences));
  } catch {
    // Nothing here is worth failing an edit over.
  }
}

export interface GridPreferences {
  /** The stored order, with any column the user has never seen appended. */
  orderOf: (ids: readonly string[]) => string[];
  widthOf: (id: string, fallback: number) => number;
  setWidth: (id: string, width: number) => void;
  move: (ids: readonly string[], from: number, to: number) => void;
  density: RowDensity;
  setDensity: (density: RowDensity) => void;
}

export function useGridPreferences(key: string): GridPreferences {
  const [preferences, setPreferences] = useState<Preferences>(() => read(key));

  const update = useCallback(
    (next: Preferences) => {
      setPreferences(next);
      write(key, next);
    },
    [key],
  );

  const orderOf = useCallback(
    (ids: readonly string[]): string[] => {
      const order = preferences.order.filter((id) => ids.includes(id));
      // A column added since the order was stored lands after the column it
      // follows by default, not at the far end where nobody looks.
      for (const [index, id] of ids.entries()) {
        if (order.includes(id)) continue;
        const before = ids
          .slice(0, index)
          .reverse()
          .find((previous) => order.includes(previous));
        order.splice(before === undefined ? 0 : order.indexOf(before) + 1, 0, id);
      }
      return order;
    },
    [preferences.order],
  );

  return {
    orderOf,
    widthOf: (id, fallback) => preferences.widths[id] ?? fallback,
    setWidth: (id, width) =>
      update({ ...preferences, widths: { ...preferences.widths, [id]: width } }),
    move: (ids, from, to) => {
      const order = orderOf(ids);
      const moved = order[from];
      if (moved === undefined) return;
      const rest = order.filter((_, index) => index !== from);
      update({ ...preferences, order: [...rest.slice(0, to), moved, ...rest.slice(to)] });
    },
    density: preferences.density,
    setDensity: (density) => update({ ...preferences, density }),
  };
}
